import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import OfferingSection from './components/OfferingSection';

const serviceIds = ['one-on-one', 'career-strategy', 'interview-prep'];

const serviceImages: Record<string, string> = {
  'one-on-one': 'https://readdy.ai/api/search-image?query=Professional%20male%20career%20mentor%20in%20his%20late%2030s%20coaching%20a%20client%20in%20a%20modern%20bright%20office%20two%20people%20deeply%20engaged%20in%20conversation%20at%20a%20clean%20wooden%20desk%20the%20mentor%20has%20short%20dark%20hair%20and%20a%20neatly%20trimmed%20beard%20wearing%20a%20smart%20blazer%20with%20open%20notebooks%20and%20a%20laptop%20warm%20natural%20light%20streaming%20through%20large%20windows%20plants%20in%20the%20background%20welcoming%20atmosphere%20editorial%20photography%20style%20rich%20navy%20blue%20and%20warm%20neutral%20tones&width=800&height=600&seq=services-detail-mentoring-male-12&orientation=landscape',
  'career-strategy': 'https://readdy.ai/api/search-image?query=Strategic%20career%20planning%20workspace%20with%20a%20large%20whiteboard%20filled%20with%20detailed%20career%20roadmap%20diagrams%20timeline%20markers%20and%20goal%20charts%20a%20clean%20desk%20with%20neatly%20arranged%20documents%20laptop%20and%20coffee%20cup%20modern%20office%20environment%20with%20soft%20natural%20lighting%20professional%20editorial%20photography&width=800&height=600&seq=services-detail-strategy-02&orientation=landscape',
  'interview-prep': 'https://readdy.ai/api/search-image?query=Modern%20bright%20professional%20office%20with%20a%20male%20interviewer%20in%20his%2030s%20conducting%20a%20mock%20interview%20with%20a%20candidate%20the%20interviewer%20has%20short%20dark%20hair%20and%20wears%20a%20smart%20blazer%20clean%20desk%20with%20CV%20documents%20and%20water%20glasses%20warm%20natural%20lighting%20from%20large%20windows%20professional%20editorial%20photography&width=800&height=600&seq=services-detail-interview-male-12&orientation=landscape',
};

const stepImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Minimalist%20icon%20illustration%20of%20two%20speech%20bubbles%20overlapping%20in%20soft%20sky%20blue%20tones%20representing%20conversation%20and%20discovery%20clean%20modern%20design%20with%20subtle%20gradient%20on%20a%20white%20background&width=80&height=80&seq=step-icon-discover-01&orientation=squarish',
  2: 'https://readdy.ai/api/search-image?query=Minimalist%20icon%20illustration%20of%20a%20roadmap%20or%20route%20map%20with%20pins%20and%20dotted%20path%20in%20soft%20sky%20blue%20tones%20representing%20strategic%20planning%20clean%20modern%20design%20with%20subtle%20gradient%20on%20a%20white%20background&width=80&height=80&seq=step-icon-roadmap-02&orientation=squarish',
  3: 'https://readdy.ai/api/search-image?query=Minimalist%20icon%20illustration%20of%20two%20people%20at%20a%20desk%20in%20deep%20discussion%20with%20a%20lightbulb%20above%20in%20soft%20sky%20blue%20tones%20representing%20coaching%20sessions%20clean%20modern%20design%20with%20subtle%20gradient%20on%20a%20white%20background&width=80&height=80&seq=step-icon-sessions-03&orientation=squarish',
  4: 'https://readdy.ai/api/search-image?query=Minimalist%20icon%20illustration%20of%20a%20helping%20hand%20and%20a%20shield%20with%20a%20checkmark%20in%20soft%20sky%20blue%20tones%20representing%20ongoing%20support%20and%20protection%20clean%20modern%20design%20with%20subtle%20gradient%20on%20a%20white%20background&width=80&height=80&seq=step-icon-support-04&orientation=squarish',
};

export default function Services() {
  const { t } = useTranslation();

  return (
    <Layout>
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-primary-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent-500 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent-400 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-300 mb-4">
            {t('services.label')}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {t('servicesPage.heroTitle')}
          </h1>
          <p className="text-base text-foreground-200 max-w-[600px] mx-auto leading-relaxed">
            {t('servicesPage.heroSubtitle')}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {serviceIds.map((id) => (
              <div
                key={id}
                className="bg-white rounded-2xl overflow-hidden border border-background-200/70 hover:border-accent-200 transition-all duration-300"
              >
                <div className="relative h-[240px] overflow-hidden">
                  <img
                    src={serviceImages[id]}
                    alt={t(`services.items.${id}.title`)}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-7">
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-primary-700 rounded-full mb-4">
                    {t(`services.items.${id}.category`)}
                  </span>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3 leading-snug">
                    {t(`services.items.${id}.title`)}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-5">
                    {t(`services.items.${id}.description`)}
                  </p>
                  <Link
                    to="/booking"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('hero.cta1')}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-10 lg:px-16 bg-accent-50">
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
            {t('servicesPage.painTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {[1, 2, 3].map((idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-background-200/70 text-left">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-emotion-sad-line text-accent-600 text-base" />
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(`servicesPage.pain${idx}`)}
                </p>
              </div>
            ))}
          </div>
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('painPoints.ctaButton')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
              {t('servicesPage.howItWorks')}
            </h2>
            <p className="text-sm text-foreground-500 max-w-[560px] mx-auto leading-relaxed">
              {t('servicesPage.howItWorksDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="relative group">
                <div className="bg-white rounded-2xl p-7 border border-background-200/70 hover:border-accent-200 transition-all duration-300 text-center h-full">
                  <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-accent-100 flex items-center justify-center overflow-hidden">
                    <img
                      src={stepImages[step]}
                      alt={t(`servicesPage.step${step}.title`)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold">
                    {step}
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                    {t(`servicesPage.step${step}.title`)}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {t(`servicesPage.step${step}.desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <OfferingSection />

      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-16 bg-primary-500">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            {t('servicesPage.cta')}
          </h2>
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-primary-700 font-semibold text-sm rounded-full hover:bg-accent-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta1')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}